import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CardActions,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { fleetSyncAPI } from '../services/api';

function SettingsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState(null);
  const [servers, setServers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingServerId, setSyncingServerId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  
  // Server dialog state
  const [serverDialogOpen, setServerDialogOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [serverForm, setServerForm] = useState({
    name: '',
    server_url: '',
    external_url: '',
    use_external_for_sync: false,
    username: '',
    password: '',
    is_enabled: true,
    priority: 0,
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serverToDelete, setServerToDelete] = useState(null);

  // Schedule form state
  const [isEnabled, setIsEnabled] = useState(true);
  const [syncHour, setSyncHour] = useState(7);
  const [syncMinute, setSyncMinute] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, serversRes, logsRes] = await Promise.all([
        fleetSyncAPI.getSettings(),
        fleetSyncAPI.getServers(),
        fleetSyncAPI.getLogs({ limit: 10 }),
      ]);
      
      const settingsData = settingsRes.data;
      setSettings(settingsData);
      setIsEnabled(settingsData.is_enabled);
      setSyncHour(settingsData.sync_hour);
      setSyncMinute(settingsData.sync_minute);
      
      setServers(serversRes.data.results || serversRes.data || []);
      setLogs(logsRes.data.results || logsRes.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await fleetSyncAPI.updateSettings({
        is_enabled: isEnabled,
        sync_hour: parseInt(syncHour),
        sync_minute: parseInt(syncMinute),
      });
      
      setSuccess('Paramètres enregistrés avec succès');
      setTimeout(() => setSuccess(null), 3000);
      loadData();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Erreur lors de l\'enregistrement des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      
      await fleetSyncAPI.triggerSync();
      setSuccess('Synchronisation démarrée');
      
      // Poll for status updates
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fleetSyncAPI.getStatus();
          if (statusRes.data.last_sync_status !== 'running') {
            clearInterval(pollInterval);
            setSyncing(false);
            loadData();
            if (statusRes.data.last_sync_status === 'success') {
              setSuccess('Synchronisation terminée avec succès');
            } else {
              setError('La synchronisation a échoué: ' + statusRes.data.last_sync_message);
            }
            setTimeout(() => setSuccess(null), 5000);
          }
        } catch (e) {
          clearInterval(pollInterval);
          setSyncing(false);
        }
      }, 2000);
      
    } catch (err) {
      console.error('Error triggering sync:', err);
      if (err.response?.status === 409) {
        setError('Une synchronisation est déjà en cours');
      } else {
        setError('Erreur lors du démarrage de la synchronisation');
      }
      setSyncing(false);
    }
  };

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setLogDialogOpen(true);
  };

  // Server management handlers
  const handleOpenServerDialog = (server = null) => {
    if (server) {
      setEditingServer(server);
      setServerForm({
        name: server.name,
        server_url: server.server_url,
        external_url: server.external_url || '',
        use_external_for_sync: server.use_external_for_sync || false,
        username: server.username || '',
        password: '',
        is_enabled: server.is_enabled,
        priority: server.priority || 0,
      });
    } else {
      setEditingServer(null);
      setServerForm({
        name: '',
        server_url: '',
        external_url: '',
        use_external_for_sync: false,
        username: '',
        password: '',
        is_enabled: true,
        priority: servers.length,
      });
    }
    setServerDialogOpen(true);
  };

  const handleCloseServerDialog = () => {
    setServerDialogOpen(false);
    setEditingServer(null);
  };

  const handleSaveServer = async () => {
    try {
      setSaving(true);
      if (editingServer) {
        await fleetSyncAPI.updateServer(editingServer.id, serverForm);
        setSuccess('Serveur mis à jour avec succès');
      } else {
        await fleetSyncAPI.createServer(serverForm);
        setSuccess('Serveur ajouté avec succès');
      }
      handleCloseServerDialog();
      loadData();
    } catch (err) {
      console.error('Error saving server:', err);
      setError(err.response?.data?.name?.[0] || 'Erreur lors de l\'enregistrement du serveur');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteServer = async () => {
    if (!serverToDelete) return;
    try {
      await fleetSyncAPI.deleteServer(serverToDelete.id);
      setSuccess('Serveur supprimé');
      setDeleteDialogOpen(false);
      setServerToDelete(null);
      loadData();
    } catch (err) {
      setError('Erreur lors de la suppression du serveur');
    }
  };

  const handleTestConnection = async () => {
    if (!editingServer) return;
    try {
      setTestingConnection(true);
      const res = await fleetSyncAPI.testServerConnection(editingServer.id);
      if (res.data.success) {
        setSuccess('Connexion réussie!');
      } else {
        setError(res.data.message || 'Échec de la connexion');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du test de connexion');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSyncServer = async (serverId) => {
    try {
      setSyncingServerId(serverId);
      await fleetSyncAPI.syncServer(serverId);
      setSuccess('Synchronisation du serveur démarrée');
      
      // Poll for status
      const pollInterval = setInterval(async () => {
        try {
          const serversRes = await fleetSyncAPI.getServers();
          const updatedServers = serversRes.data.results || serversRes.data || [];
          const server = updatedServers.find(s => s.id === serverId);
          
          if (server && server.last_sync_status !== 'running') {
            clearInterval(pollInterval);
            setSyncingServerId(null);
            loadData();
          }
        } catch (e) {
          clearInterval(pollInterval);
          setSyncingServerId(null);
        }
      }, 2000);
    } catch (err) {
      setSyncingServerId(null);
      if (err.response?.status === 409) {
        setError('Une synchronisation est déjà en cours pour ce serveur');
      } else {
        setError('Erreur lors du démarrage de la synchronisation');
      }
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'success':
        return <Chip label="Succès" color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'partial':
        return <Chip label="Partiel" color="warning" size="small" icon={<ErrorIcon />} />;
      case 'failed':
        return <Chip label="Échec" color="error" size="small" icon={<ErrorIcon />} />;
      case 'running':
        return <Chip label="En cours" color="info" size="small" icon={<SyncIcon />} />;
      case 'started':
        return <Chip label="Démarré" color="warning" size="small" />;
      case 'never':
        return <Chip label="Jamais" color="default" size="small" />;
      default:
        return <Chip label={status || 'N/A'} size="small" />;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Paramètres de Synchronisation
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<StorageIcon />} label="Serveurs" iconPosition="start" />
          <Tab icon={<ScheduleIcon />} label="Planification" iconPosition="start" />
          <Tab icon={<HistoryIcon />} label="Historique" iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab 0: Servers */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Serveurs Fleet</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenServerDialog()}
              >
                Ajouter un Serveur
              </Button>
            </Box>
          </Grid>

          {servers.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <StorageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Aucun serveur configuré
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Ajoutez un serveur Traccar pour commencer la synchronisation
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenServerDialog()}
                  >
                    Ajouter un Serveur
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            servers.map((server) => (
              <Grid item xs={12} md={6} key={server.id}>
                <Card sx={{ opacity: server.is_enabled ? 1 : 0.7 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="h6">{server.name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {server.server_url}
                        </Typography>
                      </Box>
                      <Box>
                        {server.is_enabled ? (
                          <Chip label="Activé" color="success" size="small" icon={<LinkIcon />} />
                        ) : (
                          <Chip label="Désactivé" color="default" size="small" icon={<LinkOffIcon />} />
                        )}
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Dernier statut:</Typography>
                        {getStatusChip(server.last_sync_status)}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Dernière sync:</Typography>
                        <Typography variant="body2">{formatDateTime(server.last_sync_at)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Appareils:</Typography>
                        <Typography variant="h6">{server.total_devices}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Utilisateurs:</Typography>
                        <Typography variant="h6">{server.total_users}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Box>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => handleOpenServerDialog(server)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => { setServerToDelete(server); setDeleteDialogOpen(true); }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      {server.user_url && (
                        <Tooltip title="Ouvrir le serveur">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => window.open(server.user_url, '_blank')}
                          >
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={syncingServerId === server.id ? <CircularProgress size={16} /> : <SyncIcon />}
                      onClick={() => handleSyncServer(server.id)}
                      disabled={!server.is_enabled || syncingServerId === server.id || server.last_sync_status === 'running'}
                    >
                      {syncingServerId === server.id ? 'Sync...' : 'Synchroniser'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}

          {/* Sync All Button */}
          {servers.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">Synchroniser Tous les Serveurs</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Lance la synchronisation sur tous les serveurs activés
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={syncing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                    onClick={handleTriggerSync}
                    disabled={syncing || settings?.last_sync_status === 'running'}
                  >
                    {syncing ? 'Synchronisation...' : 'Synchroniser Tout'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Tab 1: Schedule */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Planification</Typography>
                </Box>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(e) => setIsEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Synchronisation automatique activée"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <TextField
                    label="Heure"
                    type="number"
                    value={syncHour}
                    onChange={(e) => setSyncHour(Math.min(23, Math.max(0, e.target.value)))}
                    inputProps={{ min: 0, max: 23 }}
                    size="small"
                    sx={{ width: 100 }}
                    disabled={!isEnabled}
                  />
                  <TextField
                    label="Minute"
                    type="number"
                    value={syncMinute}
                    onChange={(e) => setSyncMinute(Math.min(59, Math.max(0, e.target.value)))}
                    inputProps={{ min: 0, max: 59 }}
                    size="small"
                    sx={{ width: 100 }}
                    disabled={!isEnabled}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  La synchronisation s'exécutera chaque jour à {String(syncHour).padStart(2, '0')}:{String(syncMinute).padStart(2, '0')}
                </Typography>

                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Sync Status Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SyncIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">État de la Synchronisation</Typography>
                  </Box>
                  <Tooltip title="Rafraîchir">
                    <IconButton onClick={loadData} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {settings && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Dernier statut:
                      </Typography>
                      {getStatusChip(settings.last_sync_status)}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Dernière synchronisation:
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(settings.last_sync_at)}
                      </Typography>
                    </Box>

                    {settings.last_sync_message && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Message:
                        </Typography>
                        <Typography variant="body2">
                          {settings.last_sync_message}
                        </Typography>
                      </Box>
                    )}

                    {settings.next_sync_time && isEnabled && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Prochaine synchronisation:
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(settings.next_sync_time)}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: History */}
      {tabValue === 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Historique des Synchronisations</Typography>
              </Box>
              <Tooltip title="Rafraîchir">
                <IconButton onClick={loadData} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Serveurs</TableCell>
                    <TableCell align="right">Utilisateurs</TableCell>
                    <TableCell align="right">Nouveaux</TableCell>
                    <TableCell align="right">Mis à jour</TableCell>
                    <TableCell align="right">Disparus</TableCell>
                    <TableCell>Erreur</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Aucun historique de synchronisation
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow
                        key={log.id}
                        hover
                        onClick={() => handleViewLog(log)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{formatDateTime(log.started_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.trigger_type === 'scheduled' ? 'Planifié' : 'Manuel'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{getStatusChip(log.status)}</TableCell>
                        <TableCell align="right">
                          {log.servers_success}/{log.servers_total}
                        </TableCell>
                        <TableCell align="right">{log.users_synced}</TableCell>
                        <TableCell align="right">{log.devices_new}</TableCell>
                        <TableCell align="right">{log.devices_updated}</TableCell>
                        <TableCell align="right">{log.devices_disappeared}</TableCell>
                        <TableCell>
                          {log.error_message ? (
                            <Tooltip title={log.error_message}>
                              <ErrorIcon color="error" fontSize="small" />
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Server Add/Edit Dialog */}
      <Dialog open={serverDialogOpen} onClose={handleCloseServerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingServer ? 'Modifier le Serveur' : 'Ajouter un Serveur'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Nom du serveur"
                fullWidth
                value={serverForm.name}
                onChange={(e) => setServerForm({ ...serverForm, name: e.target.value })}
                placeholder="Ex: Serveur Principal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="URL Interne (API)"
                fullWidth
                value={serverForm.server_url}
                onChange={(e) => setServerForm({ ...serverForm, server_url: e.target.value })}
                placeholder="http://192.168.1.100:8082/api"
                helperText="URL interne pour la synchronisation (avec /api)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="URL Externe (pour utilisateurs)"
                fullWidth
                value={serverForm.external_url}
                onChange={(e) => setServerForm({ ...serverForm, external_url: e.target.value })}
                placeholder="https://traccar.example.com"
                helperText="URL externe accessible par les utilisateurs (sans /api)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={serverForm.use_external_for_sync}
                    onChange={(e) => setServerForm({ ...serverForm, use_external_for_sync: e.target.checked })}
                  />
                }
                label="Utiliser l'URL externe pour la synchronisation"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nom d'utilisateur"
                fullWidth
                value={serverForm.username}
                onChange={(e) => setServerForm({ ...serverForm, username: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mot de passe"
                type="password"
                fullWidth
                value={serverForm.password}
                onChange={(e) => setServerForm({ ...serverForm, password: e.target.value })}
                helperText={editingServer ? "Laissez vide pour conserver l'actuel" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Priorité"
                type="number"
                fullWidth
                value={serverForm.priority}
                onChange={(e) => setServerForm({ ...serverForm, priority: parseInt(e.target.value) || 0 })}
                helperText="0 = plus haute priorité"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={serverForm.is_enabled}
                    onChange={(e) => setServerForm({ ...serverForm, is_enabled: e.target.checked })}
                  />
                }
                label="Activé"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editingServer && (
            <Button
              onClick={handleTestConnection}
              disabled={testingConnection}
              startIcon={testingConnection ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              Tester la connexion
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseServerDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSaveServer}
            disabled={saving || !serverForm.name || !serverForm.server_url}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le serveur "{serverToDelete?.name}" ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button color="error" onClick={handleDeleteServer}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Log Detail Dialog */}
      <Dialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Détails de la Synchronisation</Typography>
            {selectedLog && getStatusChip(selectedLog.status)}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2}>
                {/* Time Information */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Démarré à:</Typography>
                        <Typography variant="body1" fontWeight="medium">{formatDateTime(selectedLog.started_at)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Terminé à:</Typography>
                        <Typography variant="body1" fontWeight="medium">{formatDateTime(selectedLog.finished_at)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Type:</Typography>
                        <Chip 
                          label={selectedLog.trigger_type === 'scheduled' ? 'Planifié' : 'Manuel'} 
                          size="small" 
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Serveurs:</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedLog.servers_success}/{selectedLog.servers_total} réussis
                        </Typography>
                      </Grid>
                      {selectedLog.triggered_by_name && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Déclenché par:</Typography>
                          <Typography variant="body1">{selectedLog.triggered_by_name}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>

                {/* Statistics */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Statistiques</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                        <Typography variant="body2">Utilisateurs</Typography>
                        <Typography variant="h4">{selectedLog.users_synced || 0}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                        <Typography variant="body2">Nouveaux</Typography>
                        <Typography variant="h4">{selectedLog.devices_new || 0}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                        <Typography variant="body2">Mis à jour</Typography>
                        <Typography variant="h4">{selectedLog.devices_updated || 0}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                        <Typography variant="body2">Réapparus</Typography>
                        <Typography variant="h4">{selectedLog.devices_reappeared || 0}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        <Typography variant="body2">Transférés</Typography>
                        <Typography variant="h4">{selectedLog.devices_transferred || 0}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                        <Typography variant="body2">Disparus</Typography>
                        <Typography variant="h4">{selectedLog.devices_disappeared || 0}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Error Information */}
                {(selectedLog.error_message || selectedLog.servers_failed > 0) && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="error" sx={{ mb: 2 }}>
                      Erreurs et Problèmes
                    </Typography>
                    
                    {selectedLog.servers_failed > 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedLog.servers_failed} serveur(s) en échec sur {selectedLog.servers_total}
                        </Typography>
                      </Alert>
                    )}
                    
                    {selectedLog.error_message && (
                      <Alert severity="error" icon={<ErrorIcon />}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Message d'erreur:</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                          {selectedLog.error_message}
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                )}

                {/* Server Details */}
                {selectedLog.server_logs && selectedLog.server_logs.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Détails par Serveur</Typography>
                    {selectedLog.server_logs.map((serverLog, idx) => (
                      <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: serverLog.status === 'failed' ? 'error.light' : 'background.default' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">{serverLog.server_name}</Typography>
                          {getStatusChip(serverLog.status)}
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Utilisateurs:</Typography>
                            <Typography variant="body2">{serverLog.users_synced || 0}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Appareils:</Typography>
                            <Typography variant="body2">{serverLog.devices_synced || 0}</Typography>
                          </Grid>
                          {serverLog.error_message && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="error" fontWeight="bold">Erreur:</Typography>
                              <Typography variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {serverLog.error_message}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Grid>
                )}

                {/* Success Message */}
                {selectedLog.status === 'success' && !selectedLog.error_message && (
                  <Grid item xs={12}>
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      <Typography variant="body2">
                        Synchronisation terminée avec succès! Tous les serveurs ont été synchronisés correctement.
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialogOpen(false)} variant="contained">Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SettingsPage;
